import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, PhoneCall, X, CheckCircle, Clock, Pause, Play, Search } from "lucide-react";

function InlineAudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-2 min-w-[160px]">
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
      />
      <button
        onClick={toggle}
        className="w-7 h-7 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center shrink-0"
      >
        {playing ? <Pause size={12} fill="white" /> : <Play size={12} fill="white" />}
      </button>

      {/* Waveform bars */}
      <div className="flex items-center gap-[2px] h-6">
        {Array.from({ length: 16 }).map((_, i) => {
          const heights = [3, 6, 10, 8, 14, 10, 6, 12, 8, 14, 6, 10, 8, 12, 6, 4];
          return (
            <div
              key={i}
              className={`w-[3px] rounded-full transition-all ${playing ? "bg-orange-500" : "bg-slate-300"}`}
              style={{
                height: `${heights[i]}px`,
                animation: playing ? `waveBar ${0.6 + (i % 4) * 0.15}s ease-in-out infinite alternate` : "none",
              }}
            />
          );
        })}
      </div>

      <span className="text-[10px] text-slate-400 whitespace-nowrap">
        {fmt(progress)}{duration ? ` / ${fmt(duration)}` : ""}
      </span>

      <style>{`
        @keyframes waveBar {
          from { transform: scaleY(0.4); }
          to   { transform: scaleY(1.4); }
        }
      `}</style>
    </div>
  );
}

const STATUS_STYLE = {
  completed: "bg-emerald-100 text-emerald-700",
  failed:    "bg-red-100 text-red-600",
  pending:   "bg-yellow-100 text-yellow-700",
  ongoing:   "bg-blue-100 text-blue-700",
};

const OUTCOME_STYLE = {
  interested:     "bg-orange-100 text-orange-700",
  not_interested: "bg-slate-100 text-slate-600",
  callback:       "bg-purple-100 text-purple-700",
  unknown:        "bg-slate-100 text-slate-500",
};

function TranscriptDrawer({ transcript, name, recordingUrl, summary, onClose }) {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const lines = (transcript || "").split("\n").filter((l) => l.trim());
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/30" onClick={() => { setSummaryOpen(false); onClose(); }} />

      {/* Session Summary panel — slides in to the left of the transcript drawer */}
      {summaryOpen && (
        <div className="fixed top-0 right-[28rem] h-full w-80 z-50 bg-white border-r border-slate-200 shadow-xl flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900">Session Summary</span>
            <button onClick={() => setSummaryOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400"><X size={15} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {recordingUrl && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Recording</p>
                <InlineAudioPlayer url={recordingUrl} />
              </div>
            )}
            {summary ? (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Call Summary</p>
                <p className="text-sm text-slate-700 leading-relaxed">{summary}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">No summary available.</p>
            )}
          </div>
        </div>
      )}

      {/* Main transcript drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md z-50 bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900 text-sm">{name}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><PhoneCall size={11} /> Call Transcript</div>
          </div>
          <div className="flex items-center gap-2">
            {(recordingUrl || summary) && (
              <button
                onClick={() => setSummaryOpen((o) => !o)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                  summaryOpen
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                Session Summary
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {lines.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center mt-8">No transcript available.</p>
          ) : lines.map((line, i) => {
            const isAssistant = line.startsWith("assistant:");
            const isUser = line.startsWith("user:");
            if (!isAssistant && !isUser) return null;
            const text = line.replace(/^(assistant|user):\s*\[?'?/, "").replace(/'?\]?\s*$/, "").trim();
            if (!text) return null;
            return (
              <div key={i} className={`flex ${isAssistant ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-snug ${
                  isAssistant ? "bg-slate-100 text-slate-800 rounded-tl-sm" : "bg-orange-500 text-white rounded-tr-sm"
                }`}>
                  <div className="text-[10px] font-semibold mb-0.5 opacity-60 uppercase">{isAssistant ? "AI" : "Customer"}</div>
                  {text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

const SERVICE_OPTIONS = ["ShipMaxx", "ShipBulk", "SellerPro", "SupplySphere", "StockBridge", "general"];
const STATUS_OPTIONS  = ["scheduled", "in_progress", "completed", "failed"];

export default function AgentCallHistory() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterService, setFilterService] = useState("");
  const [filterPhone, setFilterPhone] = useState("");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: "500" });
        if (filterStatus) params.set("status", filterStatus);
        const { data } = await api.get(`/ai-calls?${params.toString()}`);
        setCalls(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load call history.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [filterStatus]);

  const visibleCalls = calls.filter((c) => {
    if (filterService && !(c.service_interest || "").includes(filterService)) return false;
    if (filterPhone && !(c.phone || "").includes(filterPhone)) return false;
    return true;
  });

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-5 flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/agent")}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Call History</h1>
            <p className="text-sm text-slate-500 mt-0.5">All AI agent calls made by Shreya</p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 shadow-sm flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
            <Search size={13} className="text-slate-400" />
            <input
              value={filterPhone}
              onChange={(e) => setFilterPhone(e.target.value)}
              placeholder="Search phone…"
              className="bg-transparent text-sm outline-none w-32"
            />
            {filterPhone && (
              <button onClick={() => setFilterPhone("")} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
            )}
          </div>

          <select
            value={filterService}
            onChange={(e) => setFilterService(e.target.value)}
            className="border border-slate-200 rounded-md text-sm px-3 py-1.5 bg-white"
          >
            <option value="">All services</option>
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-200 rounded-md text-sm px-3 py-1.5 bg-white"
          >
            <option value="">All status</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          {(filterPhone || filterService || filterStatus) && (
            <button
              onClick={() => { setFilterPhone(""); setFilterService(""); setFilterStatus(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <X size={12} /> Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-slate-400">{visibleCalls.length} call{visibleCalls.length !== 1 ? "s" : ""}</span>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    {["Customer", "Phone", "Status", "Outcome", "Service Interest", "Triggered By", "Callback", "Called At", "Completed At", "Recording", "Transcript"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleCalls.length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-16 text-center text-sm text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <PhoneCall size={28} className="text-slate-300" />
                          No calls found.
                        </div>
                      </td>
                    </tr>
                  )}
                  {visibleCalls.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-orange-50/20 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{c.customer_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.phone || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${STATUS_STYLE[c.status] || "bg-slate-100 text-slate-500"}`}>
                          {c.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {c.outcome ? (
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${OUTCOME_STYLE[c.outcome] || "bg-slate-100 text-slate-500"}`}>
                            {c.outcome}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate">{c.service_interest || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{c.triggered_by === "cron_new" ? "Shreya" : (c.triggered_by || "—")}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {c.callback_time ? (
                          <div className="flex items-center gap-1 text-xs text-slate-600">
                            <Clock size={12} className="text-slate-400" />
                            {new Date(c.callback_time).toLocaleString()}
                            {c.callback_done && <CheckCircle size={12} className="text-emerald-500 ml-1" />}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {c.completed_at ? new Date(c.completed_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {c.recording_url
                          ? <InlineAudioPlayer url={c.recording_url} />
                          : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {c.transcript ? (
                          <button
                            onClick={() => setTranscript({ text: c.transcript, name: c.customer_name || "Customer", recordingUrl: c.recording_url || null, summary: c.session_summary || null })}
                            className="text-xs font-medium text-orange-600 hover:text-orange-700 underline underline-offset-2"
                          >
                            View
                          </button>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {transcript && (
        <TranscriptDrawer
          transcript={transcript.text}
          name={transcript.name}
          recordingUrl={transcript.recordingUrl}
          summary={transcript.summary}
          onClose={() => setTranscript(null)}
        />
      )}
    </Layout>
  );
}
